<template>
  <v-card>
    <v-card-title>Datos de factura</v-card-title>
    <v-card-subtitle>Ingrese los datos de la factura</v-card-subtitle>
    <v-card-text>
      <div v-if="summary" class="invoice-summary">
        <div class="invoice-summary-orders">
          {{
            $tc('invoice.summary.orders', summary.ordersCount, {
              count: summary.ordersCount,
            })
          }}
        </div>
        <div class="invoice-summary-line">
          <span>{{ $t('invoice.summary.subtotal') }}</span>
          <span>
            {{ currency.symbol }}{{ displayPrice(summary.subtotal, true) }}
          </span>
        </div>
        <div
          v-if="summary.excludedTotal > 0"
          class="invoice-summary-line invoice-summary-line--excluded"
        >
          <span>{{ $t('invoice.summary.excluded') }}</span>
          <span>
            -{{ currency.symbol
            }}{{ displayPrice(summary.excludedTotal, true) }}
          </span>
        </div>
        <div class="invoice-summary-line invoice-summary-line--total">
          <span>{{ $t('invoice.summary.total') }}</span>
          <span>
            {{ currency.symbol }}{{ displayPrice(summary.invoiceTotal, true) }}
          </span>
        </div>
      </div>
      <v-form @submit.prevent v-model="valid">
        <v-select
          v-model="invoiceData.invoiceType"
          label="Tipo de factura"
          outlined
          dense
          :items="invoiceTypes"
        ></v-select>
        <template v-if="invoiceData.invoiceType !== 11">
          <v-select
            v-model="invoiceData.idType"
            label="Tipo de documento"
            outlined
            dense
            :items="idTypes"
          ></v-select>
          <v-text-field
            v-if="invoiceData.idType !== 99"
            v-model="invoiceData.id"
            label="Número de documento"
            outlined
            dense
            autofocus
            @keypress.enter.prevent="submit"
            :rules="$rule('required')"
          ></v-text-field>
          <div v-if="showLookup" class="cuit-lookup">
            <div v-if="lookupState === 'loading'" class="cuit-lookup-loading">
              <v-progress-circular indeterminate size="14" width="2" />
              <span>{{ $t('invoice.lookup.loading') }}</span>
            </div>
            <template v-else-if="lookupState === 'found' && taxpayer">
              <div class="cuit-lookup-found">
                <v-icon small color="success">mdi-check-circle</v-icon>
                <span class="cuit-lookup-name">{{
                  taxpayer.name || taxpayer.cuit
                }}</span>
                <span
                  v-if="taxpayer.condicionIvaLabel"
                  class="cuit-lookup-cond"
                >
                  {{ taxpayer.condicionIvaLabel }}
                </span>
              </div>
              <div v-if="taxpayer.constanciaError" class="cuit-lookup-warning">
                {{ taxpayer.constanciaError }}
              </div>
            </template>
            <div
              v-else-if="lookupState === 'notFound'"
              class="cuit-lookup-warning"
            >
              {{ $t('invoice.lookup.notFound') }}
            </div>
            <div v-else-if="lookupState === 'error'" class="cuit-lookup-muted">
              {{ $t('invoice.lookup.unavailable') }}
            </div>
            <div v-if="lookupWarningKey" class="cuit-lookup-warning">
              {{ $t(lookupWarningKey) }}
            </div>
          </div>
        </template>
      </v-form>
    </v-card-text>
    <v-card-actions class="justify-end">
      <v-btn text :disabled="loading" @click="$emit('close')">Cancelar</v-btn>
      <v-btn
        color="primary"
        @click="submit"
        :loading="loading"
        :disabled="!valid"
        >Aceptar</v-btn
      >
    </v-card-actions>
  </v-card>
</template>

<script lang="ts">
  import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
  import { vueData } from '@/VueData';
  import { displayPrice, debounce } from '@/utils';
  import { normalizeCuit } from '@/lib/invoicing/cuit';
  import AfipTaxpayerService from '@/api/services/invoicing/afip-taxpayer.service';
  import { ITaxpayerInfo } from '@/interfaces/ITaxpayerLookup';

  type TLookupState = 'idle' | 'loading' | 'found' | 'notFound' | 'error';

  export interface IInvoiceModalSummary {
    ordersCount: number;
    subtotal: number;
    excludedTotal: number;
    invoiceTotal: number;
  }

  @Component
  export default class InputModal extends Vue {
    // Invoice transparency: what will actually be invoiced, with the comps
    // (invitación/empleado) exclusion surfaced before emitting.
    @Prop({ default: null }) summary: IInvoiceModalSummary | null;

    displayPrice = displayPrice;

    invoiceData = {
      invoiceType: 11,
      idType: 80,
      id: '',
    };
    loading = false;
    valid = false;

    // CUIT -> razón social verification (non-blocking; ARCA is the authority).
    lookupState: TLookupState = 'idle';
    taxpayer: ITaxpayerInfo | null = null;
    private lookupSeq = 0;
    private debouncedLookup: () => void = () => {};

    invoiceTypes = [
      { value: 1, text: 'Factura A' },
      { value: 6, text: 'Factura B' },
      { value: 11, text: 'Ticket fiscal (Factura C)' },
      //{ value: 8, text: 'Mal' },
    ];

    private allIdTypes = [
      { value: 80, text: 'CUIT' },
      { value: 86, text: 'CUIL' },
      { value: 96, text: 'DNI' },
      { value: 99, text: 'Consumidor Final' },
    ];

    /** Factura A only admits CUIT receptors (WSFE rejects DocTipo != 80). */
    get idTypes() {
      if (this.invoiceData.invoiceType === 1) {
        return this.allIdTypes.filter(t => t.value === 80);
      }
      return this.allIdTypes;
    }

    get currency() {
      return vueData.currency;
    }

    /** CUIT valid for lookup only when idType 80 and not Factura C. */
    get eligibleCuit(): string | null {
      if (this.invoiceData.invoiceType === 11) return null;
      if (this.invoiceData.idType !== 80) return null;
      return normalizeCuit(this.invoiceData.id);
    }

    get showLookup(): boolean {
      return (
        this.invoiceData.invoiceType !== 11 &&
        this.invoiceData.idType === 80 &&
        this.lookupState !== 'idle'
      );
    }

    get lookupWarningKey(): string | null {
      if (this.invoiceData.invoiceType === 1) {
        // Factura A: only RI (1) and Monotributo (6) are safe receptors.
        if (this.lookupState === 'notFound')
          return 'invoice.lookup.aRejectWarning';
        if (
          this.lookupState === 'found' &&
          this.taxpayer &&
          this.taxpayer.condicionIva !== 1 &&
          this.taxpayer.condicionIva !== 6
        ) {
          return 'invoice.lookup.aRejectWarning';
        }
      }
      if (
        this.invoiceData.invoiceType === 6 &&
        this.lookupState === 'found' &&
        this.taxpayer &&
        (this.taxpayer.condicionIva === 1 || this.taxpayer.condicionIva === 6)
      ) {
        return 'invoice.lookup.suggestA';
      }
      return null;
    }

    async created() {
      this.debouncedLookup = debounce(() => this.runLookup(), 400);
    }

    @Watch('invoiceData.invoiceType')
    onInvoiceTypeChanged(type: number) {
      if (type === 1) this.invoiceData.idType = 80;
    }

    @Watch('invoiceData.id')
    @Watch('invoiceData.idType')
    @Watch('invoiceData.invoiceType')
    onLookupInputsChanged() {
      // CUIT/CUIL fields accept digits only (users paste 20-11111111-2).
      if (this.invoiceData.idType === 80 || this.invoiceData.idType === 86) {
        const digits = this.invoiceData.id.replace(/\D/g, '');
        if (digits !== this.invoiceData.id) {
          this.invoiceData.id = digits;
          return; // the assignment re-triggers this watcher
        }
      }

      if (!this.eligibleCuit) {
        this.lookupSeq++; // invalidate any in-flight response
        this.lookupState = 'idle';
        this.taxpayer = null;
        return;
      }

      this.lookupState = 'loading';
      this.taxpayer = null;
      this.debouncedLookup();
    }

    async runLookup() {
      const cuit = this.eligibleCuit;
      if (!cuit) return;
      const seq = ++this.lookupSeq;

      try {
        const result = await AfipTaxpayerService.lookup(cuit);
        if (seq !== this.lookupSeq || cuit !== this.eligibleCuit) return;
        if (result.found) {
          this.taxpayer = result.taxpayer;
          this.lookupState = 'found';
        } else {
          this.taxpayer = null;
          this.lookupState = 'notFound';
        }
      } catch (e) {
        if (seq !== this.lookupSeq || cuit !== this.eligibleCuit) return;
        this.taxpayer = null;
        this.lookupState = 'error';
      }
    }

    async submit() {
      if (this.loading) return;
      try {
        this.loading = true;
        await (<Function>this.$parent.$listeners.submit)(this.invoiceData);
      } finally {
        this.loading = false;
      }
    }
  }
</script>

<style lang="scss" scoped>
  .invoice-summary {
    @apply tw-mb-4 tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-bg-gray-50 tw-px-4 tw-py-3;

    .invoice-summary-orders {
      @apply tw-mb-2 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-gray-500;
    }

    .invoice-summary-line {
      @apply tw-flex tw-items-center tw-justify-between tw-text-sm tw-text-gray-700;

      & + .invoice-summary-line {
        @apply tw-mt-1;
      }

      &--excluded {
        @apply tw-text-orange-700;
      }

      &--total {
        @apply tw-mt-2 tw-border-0 tw-border-t tw-border-solid tw-border-gray-200 tw-pt-2 tw-text-base tw-font-bold tw-text-gray-900;
      }
    }
  }

  .cuit-lookup {
    @apply tw--mt-2 tw-mb-3 tw-text-sm;

    .cuit-lookup-loading {
      @apply tw-flex tw-items-center tw-gap-2 tw-text-gray-500;
    }

    .cuit-lookup-found {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;

      .cuit-lookup-name {
        @apply tw-font-semibold tw-text-gray-900;
      }

      .cuit-lookup-cond {
        @apply tw-rounded tw-bg-gray-100 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-gray-600;
      }
    }

    .cuit-lookup-warning {
      @apply tw-mt-1 tw-text-xs tw-text-orange-700;
    }

    .cuit-lookup-muted {
      @apply tw-text-xs tw-text-gray-500;
    }
  }
</style>
